import { Link } from "react-router-dom";
import {
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  Banknote,
  Calendar,
  CreditCard,
  RefreshCcw,
  ShoppingCart,
  Ticket as TicketIcon,
  TrendingUp,
  Users,
} from "lucide-react";
import {
  type CurrencyCode,
  type DashboardCustomerStats,
  type DashboardKpis,
  type DashboardOccupancyItem,
  type DashboardPaymentBreakdownItem,
  type DashboardRecentOrder,
  type DashboardRefundAnalysis,
  type DashboardTopEvent,
} from "@ticketnjoy/types";

import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { usePaymentProviderLabel } from "@/lib/enum-labels";
import { ISTANBUL_TZ, useFormat } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";


const ORDER_STATUS_VARIANT: Record<
  DashboardRecentOrder["status"],
  "default" | "secondary" | "warning" | "success" | "destructive"
> = {
  Paid: "success",
  Pending: "warning",
  Cancelled: "secondary",
  Expired: "secondary",
  Refunded: "destructive",
  PartiallyRefunded: "warning",
};

/* ─────────────────────────── KPI grid ─────────────────────────── */

export function KpiCards({
  kpis,
  currency,
}: {
  kpis: DashboardKpis;
  currency: CurrencyCode;
}) {
  const t = useT("reportCards.kpi");
  const fmt = useFormat();
  const cards = [
    {
      label: t("revenue"),
      value: fmt.money(kpis.revenue, currency),
      hint: t("orders", { count: kpis.ordersPaid }),
      icon: Banknote,
      tone: "primary" as const,
    },
    {
      label: t("net"),
      value: fmt.money(kpis.organizerNet, currency),
      hint: t("netHint"),
      icon: TrendingUp,
      tone: "default" as const,
    },
    {
      label: t("tickets"),
      value: fmt.number(kpis.ticketsSold),
      hint: t("averageOrder", { value: fmt.money(kpis.averageOrderValue, currency) }),
      icon: TicketIcon,
      tone: "default" as const,
    },
    {
      label: t("pending"),
      value: fmt.number(kpis.ordersPending),
      hint: kpis.ordersPending > 0 ? t("pendingHint") : t("pendingNone"),
      icon: ShoppingCart,
      tone: kpis.ordersPending > 0 ? ("warning" as const) : ("default" as const),
    },
  ];

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((c) => (
        <Card key={c.label}>
          <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
            <CardDescription>{c.label}</CardDescription>
            <c.icon
              className={cn(
                "h-4 w-4 text-muted-foreground",
                c.tone === "primary" && "text-primary",
                c.tone === "warning" && "text-amber-500",
              )}
            />
          </CardHeader>
          <CardContent>
            <div
              className={cn(
                "text-2xl font-semibold tracking-tight",
                c.tone === "primary" && "text-primary",
                c.tone === "warning" && "text-amber-600 dark:text-amber-400",
              )}
            >
              {c.value}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{c.hint}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/* ─────────────────────────── Top events ─────────────────────────── */

export function TopEventsCard({
  events,
  currency,
}: {
  events: readonly DashboardTopEvent[];
  currency: CurrencyCode;
}) {
  const t = useT("reportCards.topEvents");
  const fmt = useFormat();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("title")}</CardTitle>
        <CardDescription>{t("description")}</CardDescription>
      </CardHeader>
      <CardContent>
        {events.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t("empty")}</p>
        ) : (
          <ul className="space-y-3">
            {events.map((e, index) => (
              <li key={e.eventId} className="flex items-center gap-3">
                <span
                  className={cn(
                    "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                    index === 0
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  {index + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/events/${e.eventSlug}`}
                    className="block truncate text-sm font-medium hover:underline"
                  >
                    {e.eventTitle}
                  </Link>
                  <p className="text-xs text-muted-foreground">
                    {t("tickets", { count: e.ticketsSold })}
                  </p>
                </div>
                <div className="text-end text-sm font-medium">
                  {fmt.money(e.revenue, currency)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────── Occupancy ─────────────────────────── */

export function OccupancyCard({
  items,
}: {
  items: readonly DashboardOccupancyItem[];
}) {
  const t = useT("reportCards.occupancy");
  const fmt = useFormat();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("title")}</CardTitle>
        <CardDescription>{t("description")}</CardDescription>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t("empty")}</p>
        ) : (
          <ul className="space-y-4">
            {items.map((item) => {
              const pct = Math.round(item.occupancyRate * 100);
              const startsLabel = fmt.date(item.startsAt, {
                day: "2-digit",
                month: "short",
                year: "numeric",
                timeZone: ISTANBUL_TZ,
              });
              return (
                <li key={item.eventId} className="space-y-1.5">
                  <div className="flex items-center justify-between gap-3">
                    <Link
                      to={`/events/${item.eventSlug}`}
                      className="min-w-0 truncate text-sm font-medium hover:underline"
                    >
                      {item.eventTitle}
                    </Link>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {t("full", { value: fmt.percent(pct) })}
                    </span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-muted">
                    <div
                      className={cn(
                        "h-full rounded-full transition-all",
                        pct >= 80
                          ? "bg-destructive"
                          : pct >= 50
                            ? "bg-amber-500"
                            : "bg-primary",
                      )}
                      style={{ width: `${Math.min(100, pct)}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {startsLabel}
                    </span>
                    <span>
                      {fmt.number(item.sold + item.reserved)} / {fmt.number(item.totalCapacity)}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────── Payment + refund + customers ─────────────────────────── */

export function PaymentBreakdownCard({
  items,
  currency,
}: {
  items: readonly DashboardPaymentBreakdownItem[];
  currency: CurrencyCode;
}) {
  const t = useT("reportCards.payments");
  const providerLabel = usePaymentProviderLabel();
  const fmt = useFormat();
  const total = items.reduce((s, i) => s + i.revenue, 0);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("title")}</CardTitle>
        <CardDescription>{t("description")}</CardDescription>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t("empty")}</p>
        ) : (
          <ul className="space-y-3">
            {items.map((item) => {
              const pct = total > 0 ? (item.revenue / total) * 100 : 0;
              const label = providerLabel(item.provider);
              return (
                <li key={item.provider} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="inline-flex items-center gap-2">
                      <CreditCard className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="font-medium">{label}</span>
                      <span className="text-xs text-muted-foreground">
                        ({fmt.number(item.orderCount)})
                      </span>
                    </span>
                    <span className="font-medium">{fmt.money(item.revenue, currency)}</span>
                  </div>
                  <div className="h-1 w-full rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary transition-all"
                      style={{ width: `${Math.min(100, pct)}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export function RefundAndCustomersCard({
  refunds,
  customers,
  currency,
}: {
  refunds: DashboardRefundAnalysis;
  customers: DashboardCustomerStats;
  currency: CurrencyCode;
}) {
  const t = useT("reportCards.customers");
  const fmt = useFormat();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("title")}</CardTitle>
        <CardDescription>{t("description")}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{t("heading")}</span>
          </div>
          <div className="grid gap-2">
            <Stat label={t("unique")} value={fmt.number(customers.uniqueCustomers)} />
            <Stat
              label={t("repeat")}
              value={fmt.number(customers.repeatCustomers)}
              hint={t("repeatHint")}
            />
            <Stat
              label={t("ltv")}
              value={fmt.money(customers.lifetimeValue, currency)}
              hint={t("ltvHint")}
            />
          </div>
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm">
            <RefreshCcw className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{t("refundHeading")}</span>
          </div>
          <div className="grid gap-2">
            <Stat label={t("refundCount")} value={fmt.number(refunds.count)} />
            <Stat label={t("refundTotal")} value={fmt.money(refunds.total, currency)} />
            <div className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2">
              <span className="text-xs text-muted-foreground">{t("refundRate")}</span>
              <span
                className={cn(
                  "inline-flex items-center gap-1 text-sm font-medium",
                  refunds.refundRate > 0.05
                    ? "text-destructive"
                    : "text-foreground",
                )}
              >
                {refunds.refundRate > 0.05 ? (
                  <ArrowUpRight className="h-3.5 w-3.5 rtl:-scale-x-100" />
                ) : (
                  <ArrowDownRight className="h-3.5 w-3.5" />
                )}
                {fmt.percent(refunds.refundRate * 100, 1)}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────── Recent orders ─────────────────────────── */

export function RecentOrdersCard({
  orders,
  currency,
}: {
  orders: readonly DashboardRecentOrder[];
  currency: CurrencyCode;
}) {
  const t = useT("reportCards.recent");
  const tCommon = useT("common");
  const tStatus = useT("orderStatus");
  const fmt = useFormat();
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base">{t("title")}</CardTitle>
            <CardDescription>{t("description")}</CardDescription>
          </div>
          <Link
            to="/orders"
            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
          >
            {tCommon("viewAll")}
            <ArrowRight className="h-3 w-3 rtl:-scale-x-100" />
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {orders.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t("empty")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b text-xs text-muted-foreground">
                <tr>
                  <th className="pb-2 text-start font-medium">{t("order")}</th>
                  <th className="pb-2 text-start font-medium">{t("event")}</th>
                  <th className="pb-2 text-start font-medium">{t("buyer")}</th>
                  <th className="pb-2 text-start font-medium">{t("status")}</th>
                  <th className="pb-2 text-end font-medium">{t("amount")}</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => {
                  return (
                    <tr key={o.orderNumber} className="border-b last:border-b-0">
                      <td className="py-2.5">
                        <Link
                          to={`/orders/${o.orderNumber}`}
                          className="font-mono text-xs font-medium hover:underline"
                        >
                          {o.orderNumber}
                        </Link>
                      </td>
                      <td className="max-w-[180px] truncate py-2.5 text-muted-foreground">
                        {o.eventTitle}
                      </td>
                      <td className="max-w-[160px] truncate py-2.5 text-muted-foreground">
                        {o.buyerName || o.buyerEmail}
                      </td>
                      <td className="py-2.5">
                        <Badge variant={ORDER_STATUS_VARIANT[o.status]}>{tStatus(o.status)}</Badge>
                      </td>
                      <td className="py-2.5 text-end font-medium">
                        {fmt.money(o.total, currency)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────── Helpers ─────────────────────────── */

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        {hint ? (
          <p className="text-[10px] text-muted-foreground/70">{hint}</p>
        ) : null}
      </div>
      <p className="text-base font-semibold tabular-nums">{value}</p>
    </div>
  );
}
